import { Directive, DoCheck, ElementRef, Injector, OnDestroy, OnInit, afterNextRender, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { DatePicker } from 'primeng/datepicker';

/** A day of the calendar that the Tab key reaches. */
const REACHABLE_DAY = '.p-datepicker-calendar td span[tabindex="0"]';
/** The month's name in the calendar's title: a button, present in every month. */
const MONTH_TITLE = '.p-datepicker-select-month';

/**
 * Makes an INLINE PrimeNG date picker walkable by keyboard. The library keeps a calendar's days reachable in two
 * steps — one day carries the Tab stop, and after the month changes the focus is put back (on a day of the new
 * month for an arrow key, on the arrow button for a press of it) — and it runs both only when a POPUP calendar's
 * panel is created. A calendar mounted inline has no such moment, so:
 *
 *   - no day was ever in the Tab order: Tab went from the month arrows past the whole grid, and a keyboard could
 *     pick no day at all;
 *   - the calendar's own month arrow, pressed, lost the focus to the page (the library draws the header again and
 *     the pressed button with it), and so did an arrow key that walked off the month's edge.
 *
 * The directive runs the library's own two routines at the moments an inline calendar needs them:
 *
 *   - whenever the month grid has been built again — at first render, and when the HOST turns the calendar or
 *     changes its bounds — one day is given the Tab stop if none has it, WITHOUT taking the focus from wherever the
 *     member is (the host's own control that turned the calendar keeps it);
 *   - whenever the calendar changes its month ITSELF, the library's focus routine runs once the new month is drawn.
 *     It knows which gesture turned the month and puts the focus where that gesture expects it;
 *   - and where that routine finds nothing to focus — an arrow key walked into a month with no day that can be
 *     picked, past the last day allowed — the focus goes to the calendar's month title instead of falling to the
 *     page behind it: the next key still belongs to the calendar.
 *
 * Tab still leaves the grid: one day holds the Tab stop, never the focus by force.
 */
@Directive({
  selector: 'p-datepicker[appInlineCalendarKeys]',
  standalone: true,
})
export class InlineCalendarKeysDirective implements OnInit, DoCheck, OnDestroy {
  private readonly picker = inject(DatePicker, { self: true });
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
  private readonly injector = inject(Injector);

  /** The month grid as last seen: the library builds a new one whenever the days on screen change. */
  private grid: unknown = null;
  private monthTurned?: Subscription;

  ngOnInit(): void {
    this.monthTurned = this.picker.onMonthChange.subscribe(() => {
      // Read now, while the gesture is still in hand: the focus is on the control that turned the month, and the
      // library forgets which gesture it was once its focus routine has run.
      const walkedByArrowKey = !!this.picker.navigationState && !this.picker.navigationState.button
        && this.host.contains(document.activeElement);
      afterNextRender(() => {
        this.picker.updateFocus();
        if (walkedByArrowKey && !this.host.contains(document.activeElement)) {
          this.host.querySelector<HTMLElement>(MONTH_TITLE)?.focus();
        }
      }, { injector: this.injector });
    });
  }

  ngDoCheck(): void {
    if (this.picker.months === this.grid) return;
    this.grid = this.picker.months;
    afterNextRender(() => this.giveADayTheTabStop(), { injector: this.injector });
  }

  ngOnDestroy(): void {
    this.monthTurned?.unsubscribe();
  }

  private giveADayTheTabStop(): void {
    // The month and year views keep their own cells reachable; only the day grid is left without a Tab stop.
    if (this.picker.currentView !== 'date' || this.host.querySelector(REACHABLE_DAY)) return;
    // The library's routine marks today's day, else the month's first one that can be picked — and would focus it.
    this.picker.preventFocus = true;
    this.picker.initFocusableCell();
  }
}
